<template>
    <div>
        <text-field
            :type="passwordType"
            :min="8"
            :max="64"
            name="password"
            class="password-field"
            required
            @created="(data) => emit(EmitEvents.CREATED, data)"
            @updated="(data) => updateField(data)"
        >
            <template #label>
                <span v-html="t(`widget.${domain}.${flow}.password.label`)" />
                <slot name="addendum" />
            </template>
            <template #append>
                <div class="toggle-password-type" :class="{ showing: passwordType === 'text' }" @click="togglePasswordType">
                    <eye-icon :line-through="passwordType === 'text'" />
                </div>
            </template>
            <template #required>
                <span v-html="t(`widget.field-error.required`)" />
            </template>
            <template #min>
                <span v-html="t(`widget.${domain}.field-error.password.minimum-length`, { amount: 8 })" />
            </template>
            <template #max>
                <span v-html="t(`widget.${domain}.field-error.password.maximum-length`, { amount: 64 })" />
            </template>
            <template #information>
                <slot name="information" />
            </template>
        </text-field>

        <div v-if="flow === 'signup'">
            <span
                v-if="fieldData.value && fieldData.failed.length === 0 && fieldData.ZXCVBNResult && !isPasswordStrong(fieldData.ZXCVBNResult)"
                class="validation-error"
                >{{ t('widget.onboarding.signup.password.strength.insufficient') }}</span
            >

            <slot name="server-validation" />

            <div class="password-strength">
                <span>{{ t('widget.onboarding.signup.password.strength') }}</span>
                <div class="indicator" :data-indication="fieldData.ZXCVBNResult?.score">
                    <div
                        v-for="index in 4"
                        :key="index"
                        :class="{
                            'indicator-bar': true,
                            'indicator-bar--active': index <= fieldData.ZXCVBNResult?.score
                        }"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { useTranslations } from '@btcdirect/composables';
import { isPasswordStrong, ValidatedFieldDataExtended } from '@btcdirect/functions';
import { EmitEvents } from '@btcdirect/shared/src/components/types';
import { TextField, ValidatedFieldData } from 'out-of-the-vue';
import { computed, ref } from 'vue';
import zxcvbn from 'zxcvbn';
import EyeIcon from '../icons/eye.vue';

const emit = defineEmits([EmitEvents.CREATED, EmitEvents.UPDATED]);

const props = defineProps({
    domain: {
        type: String,
        required: true
    }
});

const { t } = useTranslations();

const flow = computed(() => (props.domain === 'authentication' ? 'sign-in' : 'signup'));

const passwordType = ref('password');
const togglePasswordType = (): void => {
    passwordType.value = passwordType.value === 'password' ? 'text' : 'password';
};

const fieldData = ref<ValidatedFieldDataExtended>({
    name: 'password',
    value: '',
    valid: false,
    failed: [],
    ZXCVBNResult: null
});

const updateField = (data: ValidatedFieldData) => {
    const ZXCVBNCheck = zxcvbn((data.value as string) ?? '', []);

    if (ZXCVBNCheck.score === 0) {
        ZXCVBNCheck.score = 1;
    }

    const result = {
        ...data,
        ZXCVBNResult: ZXCVBNCheck
    };

    fieldData.value = result;
    emit(EmitEvents.UPDATED, result);
};
</script>

<style lang="scss" scoped>
@use '@@scss/flex';
@use '@@scss/fonts';

.toggle-password-type {
    @include flex.flex;
    @include flex.align(center, center);
    align-self: stretch;
    width: 3.25em;
    cursor: pointer;
    transition: color 300ms ease;
    color: var(--ootv-input-prepend-append-color-darker);
}

.password-field {
    :deep(footer.information) {
        @include fonts.gt-walsheim-medium;
        @include fonts.font(1rem, 2em);
        color: var(--btcdirect-blue);
    }
}

.password-strength {
    @include flex.flex;
    gap: 1rem;
    margin-top: 0.5rem;

    > span {
        white-space: nowrap;
    }

    .indicator {
        @include flex.flex;
        align-items: center;
        width: 100%;
        gap: 5px;

        .indicator-bar {
            height: 5px;
            width: 100%;
            background: var(--separator-color);
            border-radius: 50px;
        }

        &[data-indication='1'] {
            .indicator-bar--active {
                background: #ff3c25;
            }
        }

        &[data-indication='2'] {
            .indicator-bar--active {
                background: #ffa500;
            }
        }

        &[data-indication='3'] {
            .indicator-bar--active {
                background: #2dcd93;
            }
        }

        &[data-indication='4'] {
            .indicator-bar--active {
                background: #16a672;
            }
        }
    }
}
</style>
